import {Box} from '@mui/material'
import type {FC} from 'react'
import WarehouseIcon from '@mui/icons-material/Warehouse'
import SchemaIcon from '@mui/icons-material/Schema'
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing'
import Picker from '@shared/components/base/inputs/Picker'
import {useCustomTranslation} from '@app/shared/hooks/useCustomTranslation'

type Obj = {id: string; name: string}

type FilterState = {
  productionAreaId: string
  lineId: string
  equipmentId: string
}

type SearchHandlers = {
  handleAreaSearch: (search: string) => void
  handleLineSearch: (search: string) => void
  handleEquipmentSearch: (search: string) => void
}

type Loading = {
  isFetchingAreas: boolean
  isFetchingLines: boolean
  isFetchingEquipments: boolean
}

type SelectedNames = {
  selectedProductionArea: string
  selectedParentForEquipment: string
}

type ParamKeys = {
  PRODUCTION_AREA: string
  LINE: string
  EQUIPMENT: string
}

type Props = {
  filterState: FilterState
  productionAreas: Obj[]
  lines: Obj[]
  equipments: Obj[]
  searchHandlers: SearchHandlers
  loading: Loading
  selectedNames: SelectedNames
  paramKeys: ParamKeys
  updateParam: (key: string, value: string) => void
  vertical?: boolean
}

const BasicHierarchyFiltersRow: FC<Props> = ({
  filterState,
  productionAreas,
  lines,
  equipments,
  searchHandlers,
  loading,
  selectedNames,
  paramKeys,
  updateParam,
  vertical = false,
}) => {
  const [t] = useCustomTranslation('common')

  // In the vertical (side-panel) layout the filters are narrow, so let their
  // dropdowns grow to fit long option names instead of clipping at 400px.
  const popoverMaxWidth = vertical ? '100%' : undefined

  // AB#5657 follow-up: BasicFilter's root Box hardcodes width:'100%', which is harmless
  // in the old nowrap row (shrinks with siblings) but claims a full wrap line once
  // flexWrap is on -- so each filter needs its own bounded-width wrapper. Row mode wraps
  // unconditionally (not behind a media query, unlike the taller multi-filter bars
  // elsewhere): 3 filters at ~180px each is a small, fixed amount of space, so it's safe
  // to just let them reflow onto a 2nd line rather than tuning a per-consumer breakpoint.
  const filterBoxSx = vertical ? undefined : {width: 200, flexShrink: 0}

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: vertical ? 'column' : 'row',
        flexWrap: vertical ? undefined : 'wrap',
        gap: vertical ? 2 : 0.5,
        rowGap: vertical ? undefined : 1,
        alignItems: vertical ? undefined : 'flex-start',
      }}
      data-testid="mbai-filter-group-box"
    >
      <Box sx={filterBoxSx}>
        <Picker
          label={t('production_area')}
          icon={<WarehouseIcon />}
          value={filterState.productionAreaId}
          onChange={(val) => updateParam(paramKeys.PRODUCTION_AREA, String(val))}
          options={productionAreas}
          onSearch={searchHandlers.handleAreaSearch}
          isLoading={loading.isFetchingAreas}
          popoverMaxWidth={popoverMaxWidth}
        />
      </Box>

      <Box sx={filterBoxSx}>
        <Picker
          label={t('lines')}
          icon={<SchemaIcon />}
          value={filterState.lineId}
          onChange={(val) => updateParam(paramKeys.LINE, String(val))}
          options={lines}
          onSearch={searchHandlers.handleLineSearch}
          isLoading={loading.isFetchingLines}
          parentName={selectedNames.selectedProductionArea}
          popoverMaxWidth={popoverMaxWidth}
        />
      </Box>

      <Box sx={filterBoxSx}>
        <Picker
          label={t('equipment.plural')}
          icon={<PrecisionManufacturingIcon />}
          value={filterState.equipmentId}
          onChange={(val) => updateParam(paramKeys.EQUIPMENT, String(val))}
          options={equipments}
          onSearch={searchHandlers.handleEquipmentSearch}
          isLoading={loading.isFetchingEquipments}
          parentName={selectedNames.selectedParentForEquipment}
          popoverMaxWidth={popoverMaxWidth}
        />
      </Box>
    </Box>
  )
}

export default BasicHierarchyFiltersRow
